import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

// Components
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs'
import { ChatOverflowMenu } from '@/chat-common/conversation_menu/ChatOverflowMenu'
import ChatTitleChangeDialog from '@/chat-common/components/dialogs/ChatTitleChangeDialog'
import ConversationDeleteConfirmDialog from '@/chat-common/components/dialogs/ConversationDeleteConfirmDialog'

// Store
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectAgentConversationFromId } from '@/agent/conversations/store/selectors'
import { selectMatterById } from '@/matter/store/selectors'

// Types
import { ChatListSidebarItem } from '@/chat-common/conversation_list/schema'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'

// Constants & utils
import { matterPaths, kRouteSegments } from '@/routing/paths'

export default function MatterBreadcrumbsChat() {
  const navigate = useNavigate()
  const { chatFeature, chatId, matterId: urlMatterId } = useParams()

  // Local state for dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteDialogItem, setDeleteDialogItem] = useState<ChatListSidebarItem | null>(null)
  const [changeTitleDialogOpen, setChangeTitleDialogOpen] = useState(false)
  const [changeTitleItem, setChangeTitleItem] = useState<ChatListSidebarItem | null>(null)

  // Dialog handlers
  const openDeleteDialog = (conversationItem: ChatListSidebarItem) => {
    setDeleteDialogItem(conversationItem)
    setDeleteDialogOpen(true)
  }

  const openChangeTitleDialog = (conversationItem: ChatListSidebarItem) => {
    setChangeTitleItem(conversationItem)
    setChangeTitleDialogOpen(true)
  }

  const shouldDisplayBreadcrumbsForFeature: boolean = chatFeature === kRouteSegments.assistant

  const agentConversation = useAppSelector((state: RootState) => selectAgentConversationFromId(state, { conversationId: chatId }))
  const conversation: ChatListSidebarItem | null = useMemo(
    () => (agentConversation ? { ...agentConversation, modified_on: agentConversation.updated_at ?? 0 } : null),
    [agentConversation]
  )

  // Get display values
  const conversationMatterId = conversation?.matter_id
  const conversationMatter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: conversationMatterId }))
  const urlMatter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: urlMatterId }))
  const title = conversation?.title ?? 'Untitled Conversation'

  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)

  // Build breadcrumb items
  const breadcrumbItems: BreadcrumbItemType[] = useMemo(() => {
    if (!initialLoadComplete) return []

    const items: BreadcrumbItemType[] = []

    // When there's no conversation (e.g. unassigned matter with no chats), show the matter name only
    if (!conversation) {
      if (urlMatter) {
        items.push({
          id: `matter-${urlMatter.id}`,
          label: urlMatter.name ?? '',
          onClick: () => navigate(matterPaths.overview(urlMatter.id)),
        })
      }
      return items
    }

    // Add matter breadcrumb if the conversation belongs to a matter
    if (conversationMatter) {
      items.push({
        id: `matter-${conversationMatter.id}`,
        label: conversationMatter.name,
        onClick: () => navigate(matterPaths.overview(conversationMatter.id)),
      })
    }

    // Add conversation title (always last, no onClick - active item)
    items.push({
      id: `conversation-${conversation.id}`,
      label: title,
    })

    return items
  }, [initialLoadComplete, conversation, conversationMatter, urlMatter, title, navigate])

  if (!shouldDisplayBreadcrumbsForFeature) {
    return null
  }

  return (
    <div className="h-[52px] lg:h-[60px] flex">
      <ChatTitleChangeDialog
        chatItem={changeTitleItem}
        dialogVisible={changeTitleDialogOpen}
        onDialogClose={() => {
          setChangeTitleDialogOpen(false)
          setChangeTitleItem(null)
        }}
      />
      <ConversationDeleteConfirmDialog
        chatItem={deleteDialogItem}
        dialogVisible={deleteDialogOpen}
        onDialogClose={() => {
          setDeleteDialogOpen(false)
          setDeleteDialogItem(null)
        }}
      />
      {/* Gap for desktop */}
      <div className="w-0 lg:w-4" />

      {/* Top bar with breadcrumbs and menu */}
      <div className="w-full pl-2 pr-3 flex justify-between items-center border-b-[0.5px] bg-brand-neutral-100 lg:bg-brand-neutral-50">
        {/* Breadcrumb navigation - left side */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <Breadcrumbs items={breadcrumbItems} maxLabelLength={30} maxActiveLabelLength={60} persistFirstItem />
        </div>

        {/* Menu - right side */}
        <div className="p-1 flex-shrink-0">
          {conversation && (
            <ChatOverflowMenu
              chatListSidebarItem={conversation}
              onSelectRename={() => openChangeTitleDialog(conversation)}
              onSelectDelete={() => openDeleteDialog(conversation)}
              iconSize={6}
            />
          )}
        </div>
      </div>
    </div>
  )
}
